import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  addEdge,
  Background,
  BackgroundVariant,
  BaseEdge,
  Controls,
  EdgeLabelRenderer,
  getSmoothStepPath,
  Handle,
  MarkerType,
  Panel,
  Position,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow,
  type Connection,
  type Edge,
  type EdgeProps,
  type EdgeTypes,
  type Node,
  type NodeTypes,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { AnimatePresence } from 'framer-motion'
import {
  ArrowLeft,
  CheckCircle,
  LayoutGrid,
  Loader2,
  Redo,
  Save,
  Undo,
  X,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import {
  useIvrAvailableResources,
  useUpdateIvrFlow,
  type IvrFlowDetail,
} from '@/api/ivrFlows'
import {
  configParDefaut,
  getCategoryByNodeType,
  getNodeDefinition,
  getOutputHandles,
} from './nodeCategories'
import { useFirstCompany } from '@/api/profile'
import { PropertiesPanel } from './PropertiesPanel'
import { NodePalette } from './NodePalette'
import { IvrAddNodePopover } from './IvrAddNodePopover'
import { applyDagreLayout } from './ivrAutoLayout'

interface IvrFlowEditorProps {
  flow: IvrFlowDetail
  onBack: () => void
}

interface IvrNodeData {
  label: string
  nodeType: string
  config: Record<string, unknown>
  connectedHandles?: Set<string>
  onAddNodeFromHandle?: (
    parentNodeId: string,
    handleId: string,
    handlePosition: 'right' | 'bottom',
    nodeType: string,
  ) => void
  [key: string]: unknown
}

// =============================================================================
// CONVERSIONS Backend ↔ ReactFlow
// =============================================================================

function convertToReactFlowNodes(
  nodes: IvrFlowDetail['nodes'],
): Node<IvrNodeData>[] {
  return nodes.map((n) => ({
    id: n.node_id,
    type: 'custom',
    position: { x: n.position_x, y: n.position_y },
    data: {
      label: n.label,
      nodeType: n.node_type,
      config: n.config,
    },
  }))
}

function convertToReactFlowEdges(edges: IvrFlowDetail['edges']): Edge[] {
  return edges.map((e) => ({
    id: e.edge_id,
    // Backend renvoie source_node_id / target_node_id / source_handle.
    // Fallback sur les champs camelCase si jamais un edge custom passe par là.
    source: e.source_node_id ?? e.source ?? '',
    target: e.target_node_id ?? e.target ?? '',
    sourceHandle: e.source_handle ?? e.sourceHandle ?? 'default',
    label: e.label || undefined,
    type: 'deletable',
    animated: true,
    style: { strokeWidth: 2, stroke: '#64748b' },
  }))
}

function convertFromReactFlowNodes(nodes: Node[]) {
  return nodes.map((node) => ({
    node_id: node.id,
    node_type: (node.data as IvrNodeData).nodeType,
    label: (node.data as IvrNodeData).label || '',
    position_x: Math.round(node.position.x),
    position_y: Math.round(node.position.y),
    config: (node.data as IvrNodeData).config || {},
  }))
}

function convertFromReactFlowEdges(edges: Edge[]) {
  return edges.map((edge) => ({
    edge_id: edge.id,
    source: edge.source,
    target: edge.target,
    sourceHandle: edge.sourceHandle || 'default',
    label: typeof edge.label === 'string' ? edge.label : '',
  }))
}

// =============================================================================
// DELETABLE EDGE
// =============================================================================

interface DeletableEdgeProps extends EdgeProps {
  data?: {
    onDelete?: (id: string) => void
  }
}

function DeletableEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  data,
}: DeletableEdgeProps) {
  const { t } = useTranslation()
  const [isHovered, setIsHovered] = useState(false)

  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  })

  return (
    <>
      <path
        d={edgePath}
        fill="none"
        strokeWidth={30}
        stroke="transparent"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{ cursor: 'pointer' }}
        className="react-flow__edge-interaction"
      />
      <BaseEdge
        path={edgePath}
        markerEnd={markerEnd}
        style={{
          ...style,
          strokeWidth: isHovered ? 3 : 2,
          stroke: isHovered ? '#ef4444' : '#64748b',
          transition: 'stroke 0.2s, stroke-width 0.2s',
        }}
      />
      <EdgeLabelRenderer>
        <div
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            pointerEvents: 'all',
            opacity: isHovered ? 1 : 0,
            transition: 'opacity 0.2s',
          }}
          className="nodrag nopan"
        >
          <button
            type="button"
            onClick={() => data?.onDelete?.(id)}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            className={cn(
              'flex items-center justify-center rounded-full shadow-md transition-all',
              isHovered
                ? 'w-7 h-7 bg-red-500 hover:bg-red-600 text-white scale-110'
                : 'w-5 h-5 bg-gray-200 hover:bg-red-400 text-gray-500 hover:text-white',
            )}
            title={t('manager.flows.editor.edge.deleteConnection')}
          >
            <X className={isHovered ? 'w-4 h-4' : 'w-3 h-3'} />
          </button>
        </div>
      </EdgeLabelRenderer>
    </>
  )
}

// =============================================================================
// CUSTOM NODE — Phase 3c-2a : render lecture seule (sans add-popover)
// =============================================================================

interface CustomNodeProps {
  data: IvrNodeData
  selected: boolean
  id: string
}

function CustomNode({ data, selected, id }: CustomNodeProps) {
  const { t } = useTranslation()
  const category = getCategoryByNodeType(data.nodeType)
  const nodeDef = getNodeDefinition(data.nodeType)

  if (!category || !nodeDef) return null

  const Icon = nodeDef.icon
  const isStart = data.nodeType === 'start'
  const outputs = getOutputHandles(data.nodeType, data.config, t)

  // Preview text selon le type
  const getPreviewText = (): string | null => {
    switch (data.nodeType) {
      case 'message': {
        const mode = data.config?.mode as string
        if (mode === 'audio' && data.config?.audio_filename) {
          return `🎵 ${(data.config.audio_filename as string).substring(0, 25)}…`
        }
        const text = data.config?.text as string
        return text
          ? `"${text.substring(0, 30)}${text.length > 30 ? '…' : ''}"`
          : t('manager.flows.editor.preview.noMessage')
      }
      case 'menu': {
        const msg = data.config?.message as string
        return msg
          ? `"${msg.substring(0, 25)}…"`
          : t('manager.flows.editor.preview.menuDtmf')
      }
      case 'queue': {
        const minutes = Math.round(Number(data.config?.max_wait || 120) / 60)
        return t('manager.flows.editor.preview.queue', { minutes })
      }
      case 'route_team': {
        if (!data.config?.patienter) return null
        const minutes = Math.round(Number(data.config?.max_wait || 120) / 60)
        return t('manager.flows.editor.preview.queue', { minutes })
      }
      case 'time_check':
        return t('manager.flows.editor.preview.timeCheck')
      case 'caller_condition': {
        const cond = data.config?.condition_type as string
        return cond === 'phone_list'
          ? t('manager.flows.editor.preview.phoneList')
          : `${t('manager.flows.editor.preview.prefix')}: ${data.config?.value || '…'}`
      }
      default:
        return null
    }
  }

  const preview = getPreviewText()

  return (
    <div
      className={cn(
        'relative min-w-[180px] rounded-lg border-2 shadow-sm transition-all duration-200 overflow-visible',
        category.bgColor,
        selected
          ? 'ring-2 ring-blue-500 ring-offset-2 shadow-lg'
          : 'hover:shadow-md',
        category.borderColor,
      )}
    >
      {!isStart && (
        <Handle
          type="target"
          position={Position.Top}
          className="!w-4 !h-4 !bg-white !border-2 !border-gray-300 hover:!border-blue-500 hover:!bg-blue-50 !transition-colors"
        />
      )}

      <div
        className={cn(
          'flex items-center gap-2 px-3 py-2 border-b',
          category.borderColor,
        )}
      >
        <div className={cn('p-1.5 rounded', category.bgColor)}>
          <Icon className={cn('w-4 h-4', category.color)} />
        </div>
        <span className="font-medium text-sm text-gray-800 truncate">
          {data.label || t(nodeDef.labelKey)}
        </span>
      </div>

      {preview && (
        <div className="px-3 py-2 text-xs text-gray-600 bg-white/50">
          {preview}
        </div>
      )}

      {/* Output handles labelisés */}
      {outputs.length > 0 && outputs.some((o) => o.label) && (
        <div
          className={cn(
            'flex flex-col gap-1 px-3 py-2 bg-white/30 rounded-b-lg',
            outputs.length > 2 && 'py-1',
          )}
        >
          {outputs.map((output) => {
            const isConnected = data.connectedHandles?.has(output.id)
            return (
              <div
                key={output.id}
                className="relative flex items-center justify-end gap-1 pr-4"
              >
                <span className="text-[10px] text-gray-500 truncate max-w-[100px]">
                  {output.label || output.id}
                </span>
                <Handle
                  type="source"
                  position={Position.Right}
                  id={output.id}
                  className="!w-3 !h-3 !bg-white !border-2 !border-gray-300 hover:!border-blue-500 hover:!bg-blue-50 !transition-colors"
                  style={{
                    position: 'absolute',
                    right: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                  }}
                />
                {!isConnected && data.onAddNodeFromHandle && (
                  <div className="nodrag nopan">
                    <IvrAddNodePopover
                      parentNodeType={data.nodeType}
                      handleId={output.id}
                      position="right"
                      onAddNode={(nodeType) =>
                        data.onAddNodeFromHandle!(
                          id,
                          output.id,
                          'right',
                          nodeType,
                        )
                      }
                    />
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Single output (start, message) */}
      {outputs.length === 1 && !outputs[0].label && (() => {
        const isConnected = data.connectedHandles?.has(outputs[0].id)
        return (
          <div className="relative">
            <Handle
              type="source"
              position={Position.Right}
              id={outputs[0].id}
              className="!w-4 !h-4 !bg-white !border-2 !border-gray-300 hover:!border-blue-500 hover:!bg-blue-50 !transition-colors"
            />
            {!isConnected && data.onAddNodeFromHandle && (
              <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-full pl-2 nodrag nopan">
                <IvrAddNodePopover
                  parentNodeType={data.nodeType}
                  handleId={outputs[0].id}
                  position="right"
                  onAddNode={(nodeType) =>
                    data.onAddNodeFromHandle!(
                      id,
                      outputs[0].id,
                      'right',
                      nodeType,
                    )
                  }
                />
              </div>
            )}
          </div>
        )
      })()}
    </div>
  )
}

// =============================================================================
// EDITOR INNER — utilise ReactFlow (doit être à l'intérieur du Provider)
// =============================================================================

const NODE_TYPES: NodeTypes = { custom: CustomNode }
const EDGE_TYPES: EdgeTypes = { deletable: DeletableEdge }

function IvrFlowEditorInner({ flow, onBack }: IvrFlowEditorProps) {
  const { t, i18n } = useTranslation()
  // La langue principale de l'entreprise (mise en service), sinon celle de l'interface.
  const { data: entreprise } = useFirstCompany()
  const langue: 'fr' | 'en' = (entreprise?.langue_principale || i18n.language).startsWith('en') ? 'en' : 'fr'
  const update = useUpdateIvrFlow(flow.id)
  const { data: resources } = useIvrAvailableResources()
  const { screenToFlowPosition, fitView, getZoom } = useReactFlow()

  const [name, setName] = useState(flow.name)
  const [nodes, setNodes, onNodesChange] = useNodesState<Node<IvrNodeData>>(
    convertToReactFlowNodes(flow.nodes),
  )
  const [edges, setEdges, onEdgesChange] = useEdgesState(
    convertToReactFlowEdges(flow.edges),
  )
  const [hasChanges, setHasChanges] = useState(false)
  const [selectedNode, setSelectedNode] =
    useState<Node<IvrNodeData> | null>(null)

  // History undo/redo
  type HistoryState = { nodes: Node<IvrNodeData>[]; edges: Edge[] }
  const [history, setHistory] = useState<HistoryState[]>([])
  const [historyIndex, setHistoryIndex] = useState(-1)
  const isUndoRedo = useRef(false)
  const lastSnapshot = useRef('')

  const pushToHistory = useCallback(() => {
    if (isUndoRedo.current) {
      isUndoRedo.current = false
      return
    }
    const snapshot = JSON.stringify({ nodes, edges })
    if (snapshot === lastSnapshot.current) return
    lastSnapshot.current = snapshot

    setHistory((prev) => {
      const next = prev.slice(0, historyIndex + 1)
      next.push({
        nodes: JSON.parse(JSON.stringify(nodes)),
        edges: JSON.parse(JSON.stringify(edges)),
      })
      if (next.length > 50) next.shift()
      return next
    })
    setHistoryIndex((i) => Math.min(i + 1, 49))
  }, [nodes, edges, historyIndex])

  useEffect(() => {
    setHasChanges(true)
    const timer = setTimeout(() => {
      pushToHistory()
    }, 500)
    return () => clearTimeout(timer)
  }, [nodes, edges, pushToHistory])

  const handleUndo = useCallback(() => {
    if (historyIndex <= 0) return
    isUndoRedo.current = true
    const prev = history[historyIndex - 1]
    if (prev) {
      setNodes(prev.nodes)
      setEdges(prev.edges)
      setHistoryIndex((i) => i - 1)
      lastSnapshot.current = JSON.stringify(prev)
    }
  }, [history, historyIndex, setNodes, setEdges])

  const handleRedo = useCallback(() => {
    if (historyIndex >= history.length - 1) return
    isUndoRedo.current = true
    const next = history[historyIndex + 1]
    if (next) {
      setNodes(next.nodes)
      setEdges(next.edges)
      setHistoryIndex((i) => i + 1)
      lastSnapshot.current = JSON.stringify(next)
    }
  }, [history, historyIndex, setNodes, setEdges])

  const canUndo = historyIndex > 0
  const canRedo = historyIndex < history.length - 1

  // Raccourcis clavier undo/redo
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault()
        handleUndo()
      }
      if (
        (e.ctrlKey || e.metaKey) &&
        (e.key === 'y' || (e.key === 'z' && e.shiftKey))
      ) {
        e.preventDefault()
        handleRedo()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [handleUndo, handleRedo])

  // Suppression d'edge
  const onDeleteEdge = useCallback(
    (edgeId: string) => {
      setEdges((eds) => eds.filter((e) => e.id !== edgeId))
    },
    [setEdges],
  )

  // Edges avec callback de suppression
  const edgesWithDelete = useMemo(
    () =>
      edges.map((edge) => ({
        ...edge,
        type: 'deletable',
        data: { ...edge.data, onDelete: onDeleteEdge },
      })),
    [edges, onDeleteEdge],
  )

  // Création d'edge
  const onConnect = useCallback(
    (params: Connection) => {
      setEdges((eds) =>
        addEdge(
          {
            ...params,
            type: 'smoothstep',
            animated: true,
            style: { strokeWidth: 2, stroke: '#64748b' },
            markerEnd: {
              type: MarkerType.ArrowClosed,
              color: '#64748b',
            },
          },
          eds,
        ),
      )
    },
    [setEdges],
  )

  // Sélection / désélection de node
  const onNodeClick = useCallback(
    (_: React.MouseEvent, node: Node) => {
      setSelectedNode(node as Node<IvrNodeData>)
    },
    [],
  )

  const onPaneClick = useCallback(() => {
    setSelectedNode(null)
  }, [])

  // Mise à jour data d'un node (label + config)
  const onUpdateNode = useCallback(
    (nodeId: string, patch: Partial<IvrNodeData>) => {
      setNodes((nds) =>
        nds.map((node) =>
          node.id === nodeId
            ? { ...node, data: { ...node.data, ...patch } }
            : node,
        ),
      )
      setSelectedNode((prev) =>
        prev?.id === nodeId
          ? { ...prev, data: { ...prev.data, ...patch } }
          : prev,
      )
    },
    [setNodes],
  )

  // Merges a config patch into the node's latest config, never the one captured at
  // render: two calls in a row (or an upload finishing after another edit) both stick.
  const onUpdateConfig = useCallback(
    (nodeId: string, patch: Record<string, unknown>) => {
      const merge = (node: Node<IvrNodeData>) => ({
        ...node,
        data: { ...node.data, config: { ...node.data.config, ...patch } },
      })
      setNodes((nds) => nds.map((node) => (node.id === nodeId ? merge(node) : node)))
      setSelectedNode((prev) => (prev?.id === nodeId ? merge(prev) : prev))
    },
    [setNodes],
  )

  // Suppression d'un node (sauf start)
  const onDeleteNode = useCallback(
    (nodeId: string) => {
      const node = nodes.find((n) => n.id === nodeId)
      if (node?.data.nodeType === 'start') return
      setNodes((nds) => nds.filter((n) => n.id !== nodeId))
      setEdges((eds) =>
        eds.filter((e) => e.source !== nodeId && e.target !== nodeId),
      )
    },
    [nodes, setNodes, setEdges],
  )

  // Drag-drop depuis la palette
  const onDragStart = useCallback(
    (event: React.DragEvent, nodeType: string) => {
      event.dataTransfer.setData('application/reactflow', nodeType)
      event.dataTransfer.effectAllowed = 'move'
    },
    [],
  )

  const onDragOver = useCallback((event: React.DragEvent) => {
    // Only steps from the palette drop on the canvas; a file is refused below.
    if (!event.dataTransfer.types.includes('application/reactflow')) return
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
  }, [])

  // A file dropped outside an audio drop zone would make the browser open it in
  // place of the editor. Refuse it instead; the drop zones accept theirs first.
  useEffect(() => {
    const refuseFile = (event: DragEvent) => {
      if (!event.dataTransfer?.types.includes('Files') || event.defaultPrevented) return
      event.preventDefault()
      if (event.type === 'dragover') event.dataTransfer.dropEffect = 'none'
    }
    window.addEventListener('dragover', refuseFile)
    window.addEventListener('drop', refuseFile)
    return () => {
      window.removeEventListener('dragover', refuseFile)
      window.removeEventListener('drop', refuseFile)
    }
  }, [])

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault()
      const type = event.dataTransfer.getData('application/reactflow')
      if (!type) return
      const nodeDef = getNodeDefinition(type)
      if (!nodeDef) return

      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      })

      const newNode: Node<IvrNodeData> = {
        id: `${type}-${Date.now()}`,
        type: 'custom',
        position,
        data: {
          label: t(nodeDef.labelKey),
          nodeType: type,
          config: configParDefaut(nodeDef, langue),
        },
      }
      setNodes((nds) => [...nds, newNode])
    },
    [screenToFlowPosition, setNodes, t, langue],
  )

  // Calcule les handles connectés par node (pour afficher/cacher le bouton "+")
  const connectedHandlesByNode = useMemo(() => {
    const map = new Map<string, Set<string>>()
    for (const edge of edges) {
      if (!map.has(edge.source)) map.set(edge.source, new Set())
      map.get(edge.source)!.add(edge.sourceHandle || 'default')
    }
    return map
  }, [edges])

  // Ajoute un node depuis un handle "+" (crée node + edge)
  const onAddNodeFromHandle = useCallback(
    (
      parentNodeId: string,
      handleId: string,
      handlePosition: 'right' | 'bottom',
      nodeType: string,
    ) => {
      const nodeDef = getNodeDefinition(nodeType)
      if (!nodeDef) return
      const parent = nodes.find((n) => n.id === parentNodeId)
      if (!parent) return

      // À côté du parent (ou dessous), puis plus bas tant qu'un bloc occupe déjà
      // la place : deux sorties d'un même bloc ne s'empilent plus l'une sur l'autre.
      const W = 260
      const H = 130
      const taille = (n: Node) => ({ w: n.measured?.width ?? W, h: n.measured?.height ?? H })
      const position = {
        x: parent.position.x + (handlePosition === 'right' ? taille(parent).w + 90 : 0),
        y: parent.position.y + (handlePosition === 'bottom' ? taille(parent).h + 60 : 0),
      }
      const occupee = () =>
        nodes.some((n) => {
          const t2 = taille(n)
          return (
            position.x < n.position.x + t2.w + 30 &&
            position.x + W + 30 > n.position.x &&
            position.y < n.position.y + t2.h + 30 &&
            position.y + H + 30 > n.position.y
          )
        })
      for (let i = 0; i < 30 && occupee(); i++) position.y += 60

      const newNode: Node<IvrNodeData> = {
        id: `${nodeType}-${Date.now()}`,
        type: 'custom',
        position,
        data: {
          label: t(nodeDef.labelKey),
          nodeType,
          config: configParDefaut(nodeDef, langue),
        },
      }
      const newEdge: Edge = {
        id: `e-${parentNodeId}-${handleId}-${newNode.id}`,
        source: parentNodeId,
        sourceHandle: handleId,
        target: newNode.id,
        type: 'deletable',
        animated: true,
        style: { strokeWidth: 2, stroke: '#475569' },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#475569' },
      }
      setNodes((nds) => [...nds, newNode])
      setEdges((eds) => [...eds, newEdge])
      // Le nouveau bloc reste à l'écran : on recadre sur le parent et lui, sans zoomer davantage.
      setTimeout(() => {
        void fitView({
          nodes: [{ id: parentNodeId }, { id: newNode.id }],
          duration: 450,
          padding: 0.35,
          maxZoom: getZoom(),
        })
      }, 60)
    },
    [nodes, setNodes, setEdges, t, langue, fitView, getZoom],
  )

  // Augmente les nodes avec onAddNodeFromHandle + connectedHandles
  const augmentedNodes = useMemo(
    () =>
      nodes.map((node) => ({
        ...node,
        data: {
          ...node.data,
          onAddNodeFromHandle,
          connectedHandles:
            connectedHandlesByNode.get(node.id) || new Set<string>(),
        },
      })),
    [nodes, onAddNodeFromHandle, connectedHandlesByNode],
  )

  // Auto-layout dagre (gauche → droite par défaut)
  const handleAutoLayout = useCallback(() => {
    const laidOutNodes = applyDagreLayout(nodes, edges) as Node<IvrNodeData>[]
    setNodes(laidOutNodes)
  }, [nodes, edges, setNodes])

  // Save (manuel OU autosave silencieux)
  const handleSave = useCallback(
    async (silent = false) => {
      try {
        await update.mutateAsync({
          name: name.trim() || flow.name,
          description: flow.description ?? undefined,
          nodes: convertFromReactFlowNodes(nodes),
          edges: convertFromReactFlowEdges(edges),
        })
        setHasChanges(false)
        if (!silent) toast.success(t('manager.flows.editor.saveSuccess'))
      } catch {
        toast.error(t('manager.flows.editor.saveError'))
      }
    },
    [update, name, flow.name, flow.description, nodes, edges, t],
  )

  // Autosave : debounce 5s après le dernier changement
  useEffect(() => {
    if (!hasChanges || update.isPending) return
    const timer = setTimeout(() => {
      void handleSave(true)
    }, 5000)
    return () => clearTimeout(timer)
  }, [hasChanges, update.isPending, handleSave])

  return (
    <div className="h-full flex flex-col bg-slate-50 dark:bg-slate-950">
      {/* Top bar */}
      <div className="flex-shrink-0 flex items-center justify-between gap-4 px-4 py-3 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
        <div className="flex items-center gap-3 min-w-0">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white -ml-2"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" />
            {t('manager.flows.editor.back')}
          </Button>

          <div className="h-6 w-px bg-slate-200 dark:bg-slate-700" />

          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="h-8 max-w-xs font-semibold"
          />

          {hasChanges ? (
            <span className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
              {t('manager.flows.editor.unsaved')}
            </span>
          ) : (
            <span className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
              <CheckCircle className="h-3 w-3" />
              {t('manager.flows.editor.saved')}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleUndo}
            disabled={!canUndo}
            title={t('manager.flows.editor.undo')}
          >
            <Undo className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleRedo}
            disabled={!canRedo}
            title={t('manager.flows.editor.redo')}
          >
            <Redo className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAutoLayout}
            disabled={nodes.length < 2}
            title={t('manager.flows.editor.autoLayout')}
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => void handleSave(false)}
            disabled={!hasChanges || update.isPending}
            className="bg-[#0A192F] text-white hover:bg-[#0A192F]/90 dark:bg-white dark:text-[#0A192F]"
          >
            {update.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {t('manager.flows.editor.save')}
          </Button>
        </div>
      </div>

      {/* Palette + Canvas + Properties Panel */}
      <div className="flex-1 min-h-0 flex">
        <NodePalette onDragStart={onDragStart} />

        <div className="flex-1 min-w-0">
          <ReactFlow
            nodes={augmentedNodes}
            edges={edgesWithDelete}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            onPaneClick={onPaneClick}
            onDragOver={onDragOver}
            onDrop={onDrop}
            nodeTypes={NODE_TYPES}
            edgeTypes={EDGE_TYPES}
            fitView
            fitViewOptions={{ padding: 0.2 }}
            minZoom={0.2}
            maxZoom={2}
            defaultEdgeOptions={{
              type: 'deletable',
              animated: true,
              style: { strokeWidth: 2, stroke: '#64748b' },
              markerEnd: { type: MarkerType.ArrowClosed, color: '#64748b' },
            }}
          >
            <Background variant={BackgroundVariant.Dots} gap={16} size={1} />
            <Controls />
            <Panel position="top-right">
              <div className="text-xs text-slate-400 bg-white/70 dark:bg-slate-900/70 backdrop-blur px-2 py-1 rounded">
                {t('manager.flows.editor.nodesCount', { count: nodes.length })}
              </div>
            </Panel>
          </ReactFlow>
        </div>

        <AnimatePresence>
          {selectedNode && (
            <PropertiesPanel
              selectedNode={selectedNode}
              onUpdateNode={onUpdateNode}
              onUpdateConfig={onUpdateConfig}
              onDeleteNode={onDeleteNode}
              onClose={() => setSelectedNode(null)}
              resources={resources}
              flowId={flow.id}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

// =============================================================================
// PUBLIC WRAPPER avec Provider
// =============================================================================

export function IvrFlowEditor(props: IvrFlowEditorProps) {
  return (
    <ReactFlowProvider>
      <IvrFlowEditorInner {...props} />
    </ReactFlowProvider>
  )
}
